/**
 * A página do convite — o primeiro contato de muita gente com o produto.
 *
 * Ela é aberta por quem clicou num link no WhatsApp e pode não ter conta, não
 * ter concluído o cadastro, ou ser a pessoa errada. Os quatro estados são
 * tratados aqui, e a ordem importa:
 *
 * 1. **Mostrar quem convidou antes de pedir login.** Pedir e-mail e senha antes
 *    de dizer de que se trata é o que faz um link de convite parecer golpe.
 * 2. **Guardar o token antes de mandar para o cadastro.** Sem isso, quem cria
 *    conta a partir do convite volta para a tela inicial e o convite se perde —
 *    o token está numa URL que ninguém mais tem.
 * 3. **Oferecer recusar com o mesmo destaque de aceitar.** O convite pode não
 *    ser para esta pessoa (SPEC REL-001), e recusar precisa ser tão fácil
 *    quanto aceitar.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import { Brand } from '@/components/Brand';
import { useSession } from '@/features/auth/useSession';
import { useMe } from '@/features/identity/useMe';
import {
  acceptInvitation,
  declineInvitation,
  previewInvitation,
} from '@/features/relationships/api';
import { rememberInvite } from '@/features/relationships/pendingInvite';
import { relationshipErrorKey } from '@/features/relationships/useRelationships';
import { ApiError } from '@/lib/apiClient';

export default function InvitePage() {
  const { t } = useTranslation(['relationships', 'common']);
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { status: sessionStatus } = useSession();
  const signedIn = sessionStatus === 'authenticated';
  // Só consulta quando há sessão: sem token, `/me` responderia 401 em toda
  // abertura de link compartilhado.
  const { data: me, isPending: mePending } = useMe({ enabled: signedIn });

  const preview = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => previewInvitation(token),
    retry: false,
    enabled: token.length > 0,
  });

  const accept = useMutation({
    mutationFn: () => acceptInvitation(token),
    onSuccess: () => navigate('/today', { replace: true }),
  });

  const decline = useMutation({
    mutationFn: () => declineInvitation(token),
    onSuccess: () => navigate('/today', { replace: true }),
  });

  if (!token) return <Navigate to="/sign-in" replace />;

  // Enquanto não se sabe o estado da conta, não se oferece aceitar.
  //
  // A versão anterior mostrava "Aceitar convite" com `me` ainda indefinido, e
  // quem não tinha concluído o onboarding tocava e recebia "este convite não é
  // mais válido" — um beco sem saída para quem só precisava terminar o
  // cadastro. O servidor passou a responder `onboarding_required`, e a tela
  // deixou de oferecer o botão antes de saber.
  const accountKnown = signedIn && !mePending;
  // Também cobre a resposta do servidor: se `/me` falhou e o aceite voltou com
  // `onboarding_required`, a tela oferece o mesmo caminho de saída em vez de
  // deixar a pessoa diante de um erro que ela não sabe resolver.
  const blockedByOnboarding =
    accept.error instanceof ApiError && accept.error.details.rule === 'onboarding_required';
  const needsOnboarding =
    blockedByOnboarding ||
    (accountKnown && me !== undefined && me.onboarding_status !== 'completed');

  function goSignUp() {
    rememberInvite(token);
    navigate('/sign-up');
  }

  function goOnboarding() {
    rememberInvite(token);
    navigate('/onboarding');
  }

  return (
    <main className="mh-shell justify-center px-5 py-10">
      <Brand />

      {preview.isPending ? (
        <p className="text-(--color-sand-700)" aria-busy="true">
          {t('common:state.loading')}
        </p>
      ) : null}

      {preview.isError ? (
        <Alert tone="error" live="assertive">
          {t(relationshipErrorKey(preview.error))}
        </Alert>
      ) : null}

      {preview.data ? (
        <section>
          <h1 className="text-3xl">
            {t('relationships:invite.title', { name: preview.data.professional_name ?? '' })}
          </h1>
          <p className="mt-2 text-(--color-sand-700)">{t('relationships:invite.body')}</p>

          {preview.data.invitee_hint ? (
            <p className="mt-4 rounded-xl bg-(--color-sand-100) p-4 text-sm text-(--color-sand-700)">
              {t('relationships:invite.forWhom', { hint: preview.data.invitee_hint })}
            </p>
          ) : null}

          {/* O aviso é curto de propósito: quem aceita precisa saber o que
              passa a ser visível, e a frase longa não é lida. */}
          <p className="mt-4 text-sm text-(--color-sand-700)">
            {t('relationships:invite.whatIsShared')}
          </p>

          {accept.isError || decline.isError ? (
            <div className="mt-4">
              <Alert tone="error" live="assertive">
                {t(relationshipErrorKey(accept.error ?? decline.error))}
              </Alert>
            </div>
          ) : null}

          {!signedIn ? (
            <div className="mt-8 flex flex-col gap-3">
              <button type="button" className="mh-button mh-button--primary" onClick={goSignUp}>
                {t('relationships:invite.createAccount')}
              </button>
              <button
                type="button"
                className="mh-button mh-button--ghost"
                onClick={() => {
                  rememberInvite(token);
                  navigate('/sign-in');
                }}
              >
                {t('relationships:invite.haveAccount')}
              </button>
            </div>
          ) : needsOnboarding ? (
            <button
              type="button"
              className="mh-button mh-button--primary mt-8 w-full"
              onClick={goOnboarding}
            >
              {t('relationships:invite.finishSignup')}
            </button>
          ) : !accountKnown ? (
            <p className="mt-8 text-(--color-sand-700)" aria-busy="true">
              {t('common:state.loading')}
            </p>
          ) : (
            <div className="mt-8 flex flex-col gap-3">
              <button
                type="button"
                className="mh-button mh-button--primary"
                disabled={accept.isPending || decline.isPending}
                onClick={() => accept.mutate()}
              >
                {accept.isPending ? t('common:state.loading') : t('relationships:invite.accept')}
              </button>
              <button
                type="button"
                className="mh-button mh-button--ghost"
                disabled={accept.isPending || decline.isPending}
                onClick={() => decline.mutate()}
              >
                {t('relationships:invite.decline')}
              </button>
            </div>
          )}
        </section>
      ) : null}
    </main>
  );
}
